import { isBaseError } from '@/core/utils/errors/base-error';
import { isValidationError } from '@/core/utils/errors/validation-error';

type ApiErrorResponse = {
  error?: {
    code?: string;
    message?: string;
  };
};

const hasResponse = (
  error: unknown,
): error is { response: { json: () => Promise<unknown> } } =>
  typeof error === 'object' &&
  error !== null &&
  'response' in error &&
  typeof (error as { response?: { json?: unknown } }).response?.json ===
    'function';

const isApiErrorResponse = (data: unknown): data is ApiErrorResponse =>
  typeof data === 'object' &&
  data !== null &&
  'error' in data &&
  typeof (data as { error?: unknown }).error === 'object';

export default async function extractErrorMessage(
  err: unknown,
  defaultMessage: string,
): Promise<string> {
  let errorMessage = defaultMessage;

  if (err instanceof Error) errorMessage = err.message;
  if (isValidationError(err)) errorMessage = err.message;
  if (isBaseError(err)) errorMessage = err.message;
  if (typeof err === 'string') errorMessage = err;

  try {
    if (hasResponse(err)) {
      const errorData = await err.response.json();

      if (isApiErrorResponse(errorData)) {
        errorMessage = errorData.error?.message || defaultMessage;
      }
    }
  } catch {
    errorMessage = defaultMessage;
  }

  return typeof errorMessage === 'string' ? errorMessage : defaultMessage;
}
